import get from 'lodash/get';

import * as Constants from '../constants';
import { ApiActions } from '../constants/ApiActions';
import { LoginActions, LoginKeys } from '../constants/Login';
import { Map } from '../lib/ImmutableUtils';
import createFilter from '../tools/ReduxPersistFilter';

const INITIAL_STATE = Map({
  [LoginKeys.DEFAULT_RESET_PASSWORD_USERNAME]: '',
  [LoginKeys.EMAIL_PASSWORD_USERNAME]: '',
  [LoginKeys.HAS_EMAILED_PASSWORD_RESET]: false,
  [LoginKeys.HAS_LOGGED_IN]: false,
  [LoginKeys.IS_ANONYMOUS]: true,
  [LoginKeys.IS_CAPTCHA_REQUIRED]: false,
  [LoginKeys.IS_LOGGED_IN_KEY]: false, // this means is the user authenticated
  [LoginKeys.IS_LOGGING_IN_KEY]: false,
  [LoginKeys.JUST_MIGRATED_WINDOWS_ACCOUNT]: false,
  [LoginKeys.LOGIN_AUTH_TOKEN]: null as $TSFixMe | null,
  [LoginKeys.LOGIN_ERROR_KEY]: '',
  [LoginKeys.LOGIN_FAIL_COUNT]: 0,
  [LoginKeys.LOGIN_FLOW_COMPLETE_KEY]: false as boolean | null,
  [LoginKeys.LOGIN_HAS_SEEN_NEW_USER_EDUCATION_KEY]: false,
  [LoginKeys.LOGIN_REDIRECT_ON_SUCCESS_KEY]: null as string | null,
  [LoginKeys.LOGIN_REMEMBER_ME_KEY]: false,
  [LoginKeys.LOGIN_USER_INITIATED]: null as boolean | null,
  [LoginKeys.PREVIOUS_USERNAME]: '',
  [LoginKeys.RESET_PASSWORD_API_ERROR]: '',
  [LoginKeys.RESET_TOKEN_INVALID]: false,
  [LoginKeys.USER_TO_RESET_PASSWORD]: '',
  [LoginKeys.USERNAME]: '',
});
export type LoginState = typeof INITIAL_STATE;

// @ts-expect-error
export const LoginFilter = createFilter({
  reducer: 'login',
  keys: [
    LoginKeys.HAS_LOGGED_IN,
    LoginKeys.IS_ANONYMOUS,
    LoginKeys.IS_LOGGED_IN_KEY,
    LoginKeys.LOGIN_AUTH_TOKEN,
    LoginKeys.LOGIN_REMEMBER_ME_KEY,
    LoginKeys.USERNAME,
  ],
  defaults: INITIAL_STATE,
});

// Update the auth token for any result that contains a listener with an auth token (e.g. upgrade account)
function augmentStateWithListenerAuthToken(
  state: LoginState,
  action: Parameters<typeof loginReducer>[1],
): LoginState {
  const lid = get(action, 'response.result');
  if (!lid) {
    return state;
  }

  const authToken = get(action, `response.entities.listeners[${lid}].authToken`);
  if (!authToken) {
    return state;
  }

  return state.set(LoginKeys.LOGIN_AUTH_TOKEN, authToken);
}

export default function loginReducer(rawState: LoginState = INITIAL_STATE, action: $TSFixMe) {
  const state = augmentStateWithListenerAuthToken(rawState, action);
  switch (action.type) {
    case LoginActions.LOGIN_FLOW_COMPLETE: {
      const { shouldPreserveLoginRedirect } = action.payload;
      if (shouldPreserveLoginRedirect) {
        // @ts-expect-error
        return state.merge(LoginKeys.LOGIN_FLOW_COMPLETE_KEY, null);
      }
      return state.merge({
        [LoginKeys.LOGIN_FLOW_COMPLETE_KEY]: true,
        [LoginKeys.LOGIN_REDIRECT_ON_SUCCESS_KEY]: null,
      });
    }

    case ApiActions.LOGIN_ATTEMPT:
    case ApiActions.ANON_LOGIN_ATTEMPT:
    case ApiActions.DEVICE_LOGIN_ATTEMPT:
      return state.merge({
        [LoginKeys.IS_LOGGING_IN_KEY]: true,
        [LoginKeys.HAS_EMAILED_PASSWORD_RESET]: false,
        [LoginKeys.DEFAULT_RESET_PASSWORD_USERNAME]: '',
        [LoginKeys.EMAIL_PASSWORD_USERNAME]: '',
        [LoginKeys.RESET_TOKEN_INVALID]: false,
        [LoginKeys.USER_TO_RESET_PASSWORD]: '',
        [LoginKeys.RESET_PASSWORD_API_ERROR]: '',
        ...(action.request?.content?.username && {
          [LoginKeys.PREVIOUS_USERNAME]: action.request.content.username,
        }),
      });

    case ApiActions.REGISTER_SUCCESS: {
      const listeners =
        (action &&
          action.response &&
          action.response.entities &&
          action.response.entities.listeners) ||
        null;
      const listenerId = (action && action.response && action.response.result) || null;
      const latestUsername = listeners[listenerId].username;
      if (!listeners || !listenerId) {
        return state.set(LoginKeys.LOGIN_ERROR_KEY, 'Unable to get login data');
      }

      return state.merge({
        [LoginKeys.IS_ANONYMOUS]: false,
        [LoginKeys.LOGIN_USER_INITIATED]: true,
        [LoginKeys.IS_LOGGING_IN_KEY]: false,
        [LoginKeys.USERNAME]: listeners[listenerId].username,
        [LoginKeys.PREVIOUS_USERNAME]: latestUsername,
        [LoginKeys.LOGIN_REMEMBER_ME_KEY]: action.params.content.keepLoggedIn,
        // after first successful login attempt, always keep this field as true, prior to which it is false
        [LoginKeys.HAS_LOGGED_IN]: true,
        [LoginKeys.IS_LOGGED_IN_KEY]: true,
        [LoginKeys.LOGIN_ERROR_KEY]: INITIAL_STATE.get(LoginKeys.LOGIN_ERROR_KEY),
        [LoginKeys.LOGIN_AUTH_TOKEN]: listeners[listenerId].authToken,
      });
    }

    case ApiActions.LOGIN_SUCCESS:
    case ApiActions.ANON_LOGIN_SUCCESS:
    case ApiActions.DEVICE_LOGIN_SUCCESS: {
      let isLoginAttempt;
      let justMigratedWindowsAccount = false;
      let isAnonymous = false;
      if (action.type === ApiActions.DEVICE_LOGIN_SUCCESS) {
        justMigratedWindowsAccount = true;
      }
      if (action.type === ApiActions.ANON_LOGIN_SUCCESS) {
        isLoginAttempt = action.isLoginAttempt;
        isAnonymous = !action.isCustomerServiceLogin;
      } else {
        isLoginAttempt = true;
      }

      /* action.response is a listeners Entity */
      const listeners =
        (action &&
          action.response &&
          action.response.entities &&
          action.response.entities.listeners) ||
        null;
      const listenerId = (action && action.response && action.response.result) || null;
      if (!listeners || !listenerId) {
        return state.set(LoginKeys.LOGIN_ERROR_KEY, 'Unable to get login data');
      }

      const latestUsername = isAnonymous
        ? state.get(LoginKeys.PREVIOUS_USERNAME)
        : listeners[listenerId].username;
      return state.merge({
        ...(action.type === ApiActions.LOGIN_SUCCESS && {
          [Constants.Login.LoginKeys.IS_CAPTCHA_REQUIRED]: INITIAL_STATE.get(
            LoginKeys.IS_CAPTCHA_REQUIRED,
          ),
          [LoginKeys.LOGIN_ERROR_KEY]: INITIAL_STATE.get(LoginKeys.LOGIN_ERROR_KEY),
        }),
        [LoginKeys.IS_ANONYMOUS]: isAnonymous,
        [LoginKeys.LOGIN_USER_INITIATED]: action.isUserInitiated,
        [LoginKeys.JUST_MIGRATED_WINDOWS_ACCOUNT]: justMigratedWindowsAccount,
        [LoginKeys.IS_LOGGING_IN_KEY]: false,
        [LoginKeys.USERNAME]: listeners[listenerId].username,
        [LoginKeys.PREVIOUS_USERNAME]: latestUsername,
        [LoginKeys.LOGIN_REMEMBER_ME_KEY]: action.remember || false,
        // after first successful login attempt, always keep this field as true, prior to which it is false
        [LoginKeys.HAS_LOGGED_IN]: !state.get(LoginKeys.HAS_LOGGED_IN)
          ? isLoginAttempt && !isAnonymous
          : true,
        [LoginKeys.IS_LOGGED_IN_KEY]: isLoginAttempt,
        [LoginKeys.LOGIN_AUTH_TOKEN]: listeners[listenerId].authToken,
      });
    }

    case ApiActions.LOGIN_FAIL:
    case ApiActions.ANON_LOGIN_FAIL: {
      const isUserInitiated = action.isUserInitiated === true;
      const isAnonLogin = action.type === ApiActions.ANON_LOGIN_FAIL;
      const failCount = state.get(LoginKeys.LOGIN_FAIL_COUNT);
      return state.merge({
        [LoginKeys.IS_LOGGING_IN_KEY]: false,
        [LoginKeys.IS_LOGGED_IN_KEY]: false,
        [LoginKeys.LOGIN_ERROR_KEY]: isUserInitiated ? action.error?.errorType || action.type : '',
        [LoginKeys.LOGIN_FAIL_COUNT]: isUserInitiated && !isAnonLogin ? failCount + 1 : failCount,
        ...(action.type === ApiActions.LOGIN_FAIL &&
          action.error.errorType === Constants.ServiceErrors.AUTH_SHOW_CAPTCHA && {
            [Constants.Login.LoginKeys.IS_CAPTCHA_REQUIRED]: true,
          }),
      });
    }

    case LoginActions.LOGIN_RESET:
      return state.merge({
        [LoginKeys.LOGIN_ERROR_KEY]: INITIAL_STATE.get(LoginKeys.LOGIN_ERROR_KEY),
        [LoginKeys.LOGIN_FAIL_COUNT]: 0,
      });

    case LoginActions.LOGIN_REMEMBER: {
      const { remember = false } = action.payload;
      return state.set(LoginKeys.LOGIN_REMEMBER_ME_KEY, remember);
    }

    case LoginActions.LOGIN_REDIRECT_ON_SUCCESS:
      return state.set(LoginKeys.LOGIN_REDIRECT_ON_SUCCESS_KEY, action.payload.afterLoginRedirect);

    case LoginActions.SET_AUTH_TOKEN_NULL:
      return state.set(LoginKeys.LOGIN_AUTH_TOKEN, null);

    case ApiActions.EMAIL_PASSWORD_SUCCESS:
      return state.merge({
        [LoginKeys.HAS_EMAILED_PASSWORD_RESET]: true,
        [LoginKeys.EMAIL_PASSWORD_USERNAME]: action.username,
      });

    case LoginActions.RESET_EMAIL_PASSWORD_SUCCESS:
      return state.set(LoginKeys.HAS_EMAILED_PASSWORD_RESET, false);

    case LoginActions.SET_RESET_USERNAME:
      return state.set(LoginKeys.DEFAULT_RESET_PASSWORD_USERNAME, action.payload.username);

    case ApiActions.VALIDATE_PASSWORD_RESET_TOKEN_SUCCESS: {
      const { username } = action.response;
      return state.merge({
        [LoginKeys.USER_TO_RESET_PASSWORD]: username,
        [LoginKeys.RESET_TOKEN_INVALID]: false,
      });
    }

    case ApiActions.VALIDATE_PASSWORD_RESET_TOKEN_FAIL:
      return state.set(LoginKeys.RESET_TOKEN_INVALID, true);

    case ApiActions.RESET_PASSWORD_SUCCESS:
      return state.merge({
        [LoginKeys.IS_CAPTCHA_REQUIRED]: INITIAL_STATE.get(LoginKeys.IS_CAPTCHA_REQUIRED),
        [LoginKeys.LOGIN_ERROR_KEY]: INITIAL_STATE.get(LoginKeys.LOGIN_ERROR_KEY),
        [LoginKeys.RESET_PASSWORD_API_ERROR]: INITIAL_STATE.get(LoginKeys.RESET_PASSWORD_API_ERROR),
        [LoginKeys.USERNAME]: state.get(LoginKeys.PREVIOUS_USERNAME),
      });

    case ApiActions.RESET_PASSWORD_FAIL:
      return state.merge({
        [LoginKeys.RESET_PASSWORD_API_ERROR]: action.error.errorMessage,
        [LoginKeys.LOGIN_ERROR_KEY]: action.error.errorType,
        ...(action.error.errorType === Constants.ServiceErrors.AUTH_SHOW_CAPTCHA && {
          [Constants.Login.LoginKeys.IS_CAPTCHA_REQUIRED]: true,
        }),
      });

    case ApiActions.ACQUIRE_PRODUCT_SUCCESS:
      return state.set(LoginKeys.LOGIN_REDIRECT_ON_SUCCESS_KEY, null);

    case Constants.Playback.ActionTypes.SET_PLAYLIST:
      return state.set(LoginKeys.JUST_MIGRATED_WINDOWS_ACCOUNT, false);

    case ApiActions.LOGOUT_SUCCESS:
      return INITIAL_STATE.merge({
        [LoginKeys.IS_LOGGED_IN_KEY]: false,
        [LoginKeys.PREVIOUS_USERNAME]: action.username,
        [LoginKeys.IS_ANONYMOUS]: true,
        [LoginKeys.HAS_LOGGED_IN]: state.get(LoginKeys.HAS_LOGGED_IN),
      });

    case ApiActions.UPDATE_ACCOUNT_SUCCESS: {
      const result = get(action, ['response', 'result'], null);
      const newUsername = get(
        action,
        ['response', 'entities', 'listeners', result, 'username'],
        null,
      );
      if (newUsername) {
        return state.set(LoginKeys.USERNAME, newUsername);
      }
      return state;
    }

    default:
      return state;
  }
}
